import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { adminListReports, adminDeleteReport, adminRegenerate, logout, currentUser } from "../lib/api";
import { ADMIN } from "../constants/testIds";
import { Edit3, Trash2, RefreshCw, Plus, LogOut, Mail } from "lucide-react";

const AdminDashboard = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    adminListReports()
      .then((d) => { setReports(d); setLoading(false); })
      .catch((e) => {
        if (e.response?.status === 401) navigate("/admin/login");
        else setLoading(false);
      });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onDelete = async (id) => {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    await adminDeleteReport(id);
    load();
  };
  const onRegen = async (id) => {
    await adminRegenerate(id);
    load();
  };
  const onLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-label text-[var(--slate)] mb-3">Admin · {currentUser()}</div>
            <h1 className="font-display text-4xl md:text-5xl text-[var(--ivory)]">Research desk.</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/inquiries" className="btn-ghost" data-testid={ADMIN.inquiriesTab}>
              <Mail size={14} /> Inquiries
            </Link>
            <Link to="/admin/reports/new" className="btn-primary" data-testid={ADMIN.newReport}>
              <Plus size={14} /> New report
            </Link>
            <button onClick={onLogout} className="btn-ghost" data-testid={ADMIN.logout}>
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="divider-rule mb-10" />

        {loading ? (
          <div className="font-mono-label text-[var(--slate)] py-12 text-center">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left font-mono-label text-[var(--slate)] border-b border-[var(--border)]">
                  <th className="py-3 pr-4">Title</th>
                  <th className="py-3 pr-4">Series</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Published</th>
                  <th className="py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="py-5 pr-4">
                      <div className="font-display text-lg text-[var(--ivory)]">{r.title}</div>
                      <div className="text-xs text-[var(--slate-dim)] mt-1">{r.slug}</div>
                    </td>
                    <td className="py-5 pr-4 text-sm text-[var(--slate)]">{r.category}</td>
                    <td className="py-5 pr-4">
                      <span className={`font-mono-label ${r.content_status === "ready" ? "text-[var(--accent)]" : r.content_status === "generating" ? "text-[var(--blue-soft)]" : "text-red-300"}`}>
                        {r.content_status}
                      </span>
                    </td>
                    <td className="py-5 pr-4 text-sm text-[var(--slate)]">{r.published ? "Yes" : "No"}</td>
                    <td className="py-5">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => onRegen(r.id)} title="Regenerate content" className="p-2 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--slate)] hover:text-[var(--accent)] transition-colors" data-testid={ADMIN.regenerate(r.id)}>
                          <RefreshCw size={14} />
                        </button>
                        <Link to={`/admin/reports/${r.id}/edit`} className="p-2 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--slate)] hover:text-[var(--accent)] transition-colors" data-testid={ADMIN.editReport(r.id)}>
                          <Edit3 size={14} />
                        </Link>
                        <button onClick={() => onDelete(r.id)} className="p-2 border border-[var(--border)] hover:border-red-400 text-[var(--slate)] hover:text-red-300 transition-colors" data-testid={ADMIN.deleteReport(r.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default AdminDashboard;
